import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../../../../ui/components/Button";
import { DialogLayout } from "../../../../ui/layouts/DialogLayout";
import { Badge } from "../../../../ui/components/Badge";
import {
  FeatherPlus,
  FeatherAlertTriangle,
  FeatherEdit,
  FeatherTrash2,
} from "@subframe/core";
import { listRoles, deleteRole } from "../../data/api/rolesApi";
import { useToast } from "../../../../shared/presentation/components";

interface Role {
  id: number;
  role_name: string;
  is_manager_required: boolean | number;
  description?: string;
}

export const RoleManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSuccess, showError } = useToast();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Refresh roles when component mounts (which happens when navigating back from edit page)
  useEffect(() => {
    fetchRoles();
  }, []);

  // Also refresh when the roles tab becomes active (handles case where component doesn't remount)
  useEffect(() => {
    const currentTab = searchParams.get("tab");
    if (currentTab === "roles" && roles.length > 0) {
      // Only refresh if we already have data (component was already mounted)
      // This prevents double-fetching on initial mount
      fetchRoles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await listRoles();
      if (response.success && response.data) {
        // Derived maker-checker roles ('ZN {first} u{id}') are written only
        // by the team-assignment board's save; editing one here would
        // full-replace its permissions and clobber the derivation.
        setRoles(
          response.data.filter((role: any) => !String(role.role_name ?? "").startsWith("ZN ")),
        );
      } else {
        showError("Failed to load roles");
      }
    } catch (error: any) {
      showError(error.message || "Failed to load roles");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    navigate("/admin/roles/add");
  };

  const handleEdit = (role: Role) => {
    navigate(`/admin/roles/edit/${role.id}`);
  };

  const handleDeleteClick = (role: Role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!roleToDelete) return;

    try {
      setDeleting(true);
      await deleteRole(roleToDelete.id);
      showSuccess("Role deleted successfully");
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
      fetchRoles(); // Refresh list
    } catch (error: any) {
      showError(error.message || "Failed to delete role");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    if (!deleting) {
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
    }
  };

  const isManagerRequired = (role: Role): boolean => {
    const value = role.is_manager_required;
    if (value === null || value === undefined) return false;
    const strValue = String(value).trim().toLowerCase();
    return (
      strValue === "1" || strValue === "true" || value === 1 || value === true
    );
  };

  return (
    <div className="flex w-full flex-col items-start gap-6 p-8">
      {/* Header */}
      <div className="flex w-full items-center justify-between">
        <div className="flex flex-col items-start gap-1">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Role Management
          </span>
          <span className="text-body font-body text-subtext-color">
            Manage roles, permissions, and review policies
          </span>
        </div>
        <Button
          variant="brand-primary"
          icon={<FeatherPlus />}
          onClick={handleCreate}
        >
          Add Role
        </Button>
      </div>

      {/* Roles Table */}
      <div className="w-full">
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <span className="text-body font-body text-subtext-color">
              Loading roles...
            </span>
          </div>
        ) : roles.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 gap-4 border border-solid border-neutral-border rounded-lg">
            <span className="text-body font-body text-subtext-color">
              No roles found. Create your first role to get started.
            </span>
            <Button variant="brand-primary" onClick={handleCreate}>
              Add Role
            </Button>
          </div>
        ) : (
          <div className="border border-solid border-neutral-border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-neutral-50 border-b border-solid border-neutral-border">
                <tr>
                  <th className="text-left p-4 text-body-bold font-body-bold text-default-font">
                    Role Name
                  </th>
                  <th className="text-left p-4 text-body-bold font-body-bold text-default-font">
                    Manager Required
                  </th>
                  <th className="text-left p-4 text-body-bold font-body-bold text-default-font">
                    Description
                  </th>
                  <th className="text-right p-4 text-body-bold font-body-bold text-default-font">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {roles.map((role) => (
                  <tr
                    key={role.id}
                    className="border-b border-solid border-neutral-border last:border-b-0 hover:bg-neutral-50"
                  >
                    <td className="p-4 text-body font-body text-default-font">
                      {role.role_name}
                    </td>
                    <td className="p-4">
                      {isManagerRequired(role) ? (
                        <Badge variant="brand">Yes</Badge>
                      ) : (
                        <Badge variant="neutral">No</Badge>
                      )}
                    </td>
                    <td className="p-4 text-body font-body text-subtext-color">
                      {role.description || "-"}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="neutral-tertiary"
                          icon={<FeatherEdit />}
                          onClick={() => handleEdit(role)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="neutral-tertiary"
                          icon={<FeatherTrash2 />}
                          onClick={() => handleDeleteClick(role)}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <DialogLayout
        open={deleteDialogOpen}
        onOpenChange={(open: boolean) => {
          if (!deleting) {
            setDeleteDialogOpen(open);
            if (!open) {
              setRoleToDelete(null);
            }
          }
        }}
      >
        <div className="flex w-[480px] flex-col items-start gap-6 bg-default-background px-8 py-6">
          <div className="flex w-full items-center gap-4">
            <div className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-error-100">
              <FeatherAlertTriangle className="text-error-600" />
            </div>
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Delete Role
              </span>
              <span className="text-body font-body text-subtext-color">
                Are you sure you want to delete this role?
              </span>
            </div>
          </div>

          {roleToDelete && (
            <div className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
              <span className="text-body-bold font-body-bold text-default-font">
                Role Details:
              </span>
              <div className="flex flex-col gap-1 text-body font-body text-subtext-color">
                <span>
                  <strong>Role Name:</strong> {roleToDelete.role_name}
                </span>
                {roleToDelete.description && (
                  <span>
                    <strong>Description:</strong> {roleToDelete.description}
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="flex w-full flex-col items-start gap-2">
            <span className="text-body-bold font-body-bold text-error-600">
              This action cannot be undone.
            </span>
            <span className="text-body font-body text-subtext-color">
              The role will be permanently deleted. If the role is assigned to
              users, deletion will fail.
            </span>
          </div>

          <div className="flex w-full items-center justify-end gap-3">
            <Button
              variant="neutral-secondary"
              onClick={handleDeleteCancel}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="brand-primary"
              onClick={handleDeleteConfirm}
              disabled={deleting}
              loading={deleting}
            >
              {deleting ? "Deleting..." : "Delete Role"}
            </Button>
          </div>
        </div>
      </DialogLayout>
    </div>
  );
};
